import { useScroll } from 'framer-motion';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useRef } from 'react';
import type { Thing } from 'schema-dts';
import { PAGES } from '@/consts';
import { TeamPerson } from '@/entities';
import { getCommonPageProps } from '@/shared/api';
import { getEmployees } from '@/shared/api/employees';
import { getPageDetail } from '@/shared/api/page-detail';
import { getQuestions } from '@/shared/api/questions';
import { getSiteSettings } from '@/shared/api/site-settings';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import AboutSection, { AboutSectionProps } from '@/shared/components/sections/AboutSection';
import FaqSection, { FaqSectionProps } from '@/shared/components/sections/FaqSection/FaqSection';
import HowItWasSection, { HowItWasSectionProps } from '@/shared/components/sections/HowItWasSection';
import QuestionsSection, { QuestionsSectionProps } from '@/shared/components/sections/QuestionsSection';
import StagesSection, { StagesSectionProps } from '@/shared/components/sections/StagesSection/StagesSection';
import TarifSection, { TarifSectionProps } from '@/shared/components/sections/TarifSection';
import Breadcrumbs from '@/shared/components/shared/Breadcrumbs';
import GradientBG from '@/shared/components/shared/GradientBG';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import { CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { filterFaqQuestionsVisibleInFaqSection } from '@/shared/lib/utils/faq-json-ld';
import { generateWebPageJsonLd } from '@/shared/lib/utils/page-json-ld';
import DraftSection, { DraftSectionProps } from '@/shared/ui/sections/DraftSection';

const formLink = 'https://forms.yandex.ru/cloud/69b2450849af47072f411314/';

const TeamPage = ({
    breadcrumbs,
    meta,
    subtitle,
    aboutSectionProps,
    howItWasSectionProps,
    tarifSectionProps,
    draftsSectionProps,
    faqSectionProps,
    stagesSectionProps,
    questionsSectionProps,
    contactPersonProps,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });

    return (
        <DefaultLayout currentPageId={PAGES.KONTAKTY} ref={ref}>
            <div className="news-page-top js-header-theme-trigger" data-theme="dark" id="top">
                <div className="wrapper">
                    <Breadcrumbs className="news-page-breadcrumbs" breadcrumbs={breadcrumbs} />
                    <div className="program-detail-title-block">
                        <div className="program-detail-title-block-text">
                            {subtitle && (
                                <h1
                                    className="program-main-article text-m"
                                    dangerouslySetInnerHTML={{ __html: tp(subtitle) }}
                                ></h1>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <AboutSection
                className="js-header-theme-trigger js-bg-trigger-dark"
                data-theme="dark"
                aboutSectionProps={aboutSectionProps}
            />

            {howItWasSectionProps.slider.images.length > 0 && (
                <HowItWasSection
                    howItWasSectionProps={howItWasSectionProps}
                    className="js-header-theme-trigger"
                    data-theme="dark"
                />
            )}

            <GradientBG scrollYProgress={scrollYProgress} />
            {draftsSectionProps.images.length > 0 && (
                <DraftSection
                    data={draftsSectionProps}
                    isReversed
                    className="js-header-theme-trigger"
                    data-theme="light"
                />
            )}

            <TarifSection tarifSectionProps={tarifSectionProps} className="js-header-theme-trigger" data-theme="dark" />

            {stagesSectionProps.stages.length > 0 && (
                <StagesSection
                    stagesSectionProps={stagesSectionProps}
                    className="js-header-theme-trigger"
                    data-theme="dark"
                />
            )}

            {filterFaqQuestionsVisibleInFaqSection(faqSectionProps.questions).length > 0 && (
                <FaqSection faqSectionProps={faqSectionProps} className="js-header-theme-trigger" data-theme="dark" />
            )}

            <QuestionsSection
                id="team-questions"
                questionsSectionProps={questionsSectionProps}
                contactPersonProps={contactPersonProps}
                className="js-header-theme-trigger"
                data-theme="dark"
            />
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-team"
                data={generateWebPageJsonLd({
                    name: subtitle || aboutSectionProps.title,
                    description: meta.description,
                    pagePath: '/team/',
                    breadcrumbs,
                    faqQuestions: faqSectionProps.questions,
                })}
            />
        </DefaultLayout>
    );
};

export default TeamPage;

type TeamPageProps = NonUndefined<
    CommonPageProps & {
        subtitle: string | null;
        aboutSectionProps: AboutSectionProps;
        howItWasSectionProps: HowItWasSectionProps;
        draftsSectionProps: DraftSectionProps;
        tarifSectionProps: TarifSectionProps;
        faqSectionProps: FaqSectionProps;
        stagesSectionProps: StagesSectionProps;
        questionsSectionProps: QuestionsSectionProps;
        contactPersonProps: TeamPerson | null;
    }
>;

export const getStaticProps: GetStaticProps<TeamPageProps> = async () => {
    const pageResponse = await getPageDetail({ params: { slug: 'team' } });

    const [commonPageProps, siteSettings, questionsResponse, employeesResponse] = await Promise.all([
        getCommonPageProps(pageResponse.data?.id ?? PAGES.TEAM),
        getSiteSettings({
            params: {
                keys: [
                    'team_first_screen_caption',
                    'team_first_screen_title',
                    'team_first_screen_description',
                    'team_first_screen_form_link_text',
                    'team_first_screen_form_link',
                    'team_about_title',
                    'team_about_description',
                    'team_about_images',
                    'team_announcement_title',
                    'team_announcement_description',
                    'team_announcement_images',
                    'team_work_title',
                    'team_work_advantages',
                    'team_selection_stages_title',
                    'team_selection_stages_stages',
                ],
            },
        }),
        getQuestions({ params: { page: pageResponse.data?.id ?? PAGES.TEAM } }),
        getEmployees({ params: { page: pageResponse.data?.id ?? PAGES.TEAM } }),
    ]);

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'team-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'Команда', href: '/team/' },
            ],
            subtitle: siteSettings.data?.team_first_screen_caption ?? 'Работа в TNF',
            aboutSectionProps: {
                title: siteSettings.data?.team_first_screen_title ?? 'Работа мечты в команде лидеров',
                description:
                    siteSettings.data?.team_first_screen_description ??
                    '<p>TNF — ключевая площадка нефтегазовой отрасли России, которая ежегодно объединяет тысячи лидеров бизнеса, экспертов и представителей власти.</p></p>Команда TNF — сплоченный коллектив профессионалов, сочетающих экспертизу в event-менеджменте, коммуникациях и лидерском развитии. Мы постоянно находимся в развитии и поиске новых решений. Создаем незабываемые события и гордимся ростом аудитории, топ-партнерами и восторженными отзывами.</p><p>В 2025 году TNF признан лучшим деловым событием России по версии MICE EXCELLENCE AWARDS.</p>',
                link: {
                    text: siteSettings.data?.team_first_screen_form_link_text ?? '',
                    href: siteSettings.data?.team_first_screen_form_link ?? '',
                },
            },
            howItWasSectionProps: {
                slider: {
                    images: siteSettings.data?.team_about_images ?? [],
                },
                title: siteSettings.data?.team_about_title ?? 'О нас',
                description:
                    siteSettings.data?.team_about_description ??
                    'Форум прошлого года стал одним из самых значимых событий нефтегазовой промышленности, объединив в Тюмени ведущих экспертов и ключевых игроков отрасли со всей страны',
            },
            draftsSectionProps: {
                title:
                    siteSettings.data?.team_announcement_title ??
                    'Масштаб форума растет, и мы ждем в команду талантливых профессионалов, готовых расти и развиваться вместе с форумом!',
                text:
                    siteSettings.data?.team_announcement_description ??
                    'Объявляем открытый конкурс по отбору специалистов в команду TNF!',
                images: siteSettings.data?.team_announcement_images ?? [],
                button: {
                    text: 'Стать частью команды TNF',
                    href: formLink,
                },
            },
            tarifSectionProps: {
                title: siteSettings.data?.team_work_title ?? 'Работа в TNF — это...',
                subtitle: '',
                button: null,
                cards: siteSettings.data?.team_work_advantages ?? [],
            },
            stagesSectionProps: {
                title: siteSettings.data?.team_selection_stages_title ?? 'Этапы отбора',
                button: {
                    text: 'Заполнить форму',
                    href: formLink,
                },
                stages:
                    (siteSettings.data?.team_selection_stages_stages ?? []).map((item, i) => ({
                        label: `${i + 1}`.padStart(2, '0'),
                        title: item.name,
                        contentItems: item.content.map((content) => ({
                            label: content.title,
                            text: content.description,
                        })),
                        status: item.caption ?? null,
                    })) ?? [],
            },
            faqSectionProps: {
                title: 'Частые вопросы',
                questions: questionsResponse.data ?? [],
            },
            questionsSectionProps: {
                title: siteSettings.data?.new_program_form_title ?? 'Остались вопросы? Напишите нам',
                description: siteSettings.data?.new_program_form_description ?? '',
            },
            contactPersonProps: employeesResponse.data?.[0] || null,
        } satisfies TeamPageProps,
        revalidate: 60,
    };
};
